import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";

import { intersection } from "lodash";

import { useAppSelector } from "app/hooks";
import { ACCESS_TO_COSTS_ENABLED } from "constants/feature-flags";
import {
  VIEW_OWN_REPORTS_ONLY,
  VIEW_OWN_SCHEDULED_ONLY
} from "constants/permissions";
import {
  ADMIN_LEVEL_GROUPS,
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS,
  GROUP_SCHEDULER,
  GROUP_SUNO
} from "constants/staff";
import { computeActiveSessionContext } from "features/app-layout/layout/practice-context-banner/practice-session-context.util";
import {
  selectAuthScheme,
  selectCurrentUser,
  selectCurrentUserToken
} from "features/auth/auth-slice";

export default function useUserRoles() {
  const currentUser = useAppSelector(selectCurrentUser);
  const token = useAppSelector(selectCurrentUserToken);
  const authScheme = useAppSelector(selectAuthScheme);

  const userRoles = useMemo(() => {
    return currentUser?.groups?.map(({ name }) => name) || [];
  }, [currentUser?.groups]);

  // A switched / impersonation session (active practice != home practice).
  const isSwitchedSession = useMemo(
    () =>
      computeActiveSessionContext({
        token,
        scheme: authScheme,
        groupNames: userRoles
      }).isSwitched,
    [token, authScheme, userRoles]
  );

  const userPermissions: { [permissionName: string]: boolean } = useMemo(() => {
    const permissions: { [permissionName: string]: boolean } = {};
    currentUser?.groups?.reduce(
      (permissionsDict: { [permissionName: string]: boolean }, group) => {
        group.permissions.forEach(({ codename }) => {
          permissionsDict[codename] = true;
        });
        return permissionsDict;
      },
      permissions
    );
    // Switched-in admins see & manage the full schedule and reports: the home-practice "view own only"
    // restrictions are meaningless across practices (the user isn't a provider in the switched practice)
    // and would otherwise hide the Providers/Staff filters and narrow the calendar and reports to the
    // user, leaving them empty. Mirrors the backend get_custom_user_permissions drop (SN-10512).
    if (isSwitchedSession) {
      delete permissions[VIEW_OWN_SCHEDULED_ONLY];
      delete permissions[VIEW_OWN_REPORTS_ONLY];
    }
    return permissions;
  }, [currentUser?.groups, isSwitchedSession]);

  const hasRole = useCallback(
    (role: string[] | string) => {
      return (
        intersection(Array.isArray(role) ? role : [role], userRoles).length > 0
      );
    },
    [userRoles]
  );

  const isSunoAdminOrBusinessOwner = useMemo(
    () => hasRole([GROUP_SUNO, GROUP_BUSINESS_OWNERS]),
    [hasRole]
  );

  const isAdminOrBusinessOwner = useMemo(
    () => hasRole(ADMIN_LEVEL_GROUPS),
    [hasRole]
  );

  const isBusinessOwner = useMemo(
    () => hasRole([GROUP_BUSINESS_OWNERS]),
    [hasRole]
  );

  const isSunoAdmin = useMemo(() => hasRole([GROUP_SUNO]), [hasRole]);
  const isAudiologist = useMemo(() => hasRole([GROUP_AUDIOLOGISTS]), [hasRole]);
  const isScheduler = useMemo(() => hasRole([GROUP_SCHEDULER]), [hasRole]);

  const flags = useFlags();
  const accessToCostsEnabled = flags[ACCESS_TO_COSTS_ENABLED];
  const canViewCosts = accessToCostsEnabled
    ? Boolean(currentUser?.access_to_costs)
    : Boolean(currentUser?.can_see_manufacturer_cost);

  const canAccessDashboards = useMemo(
    () =>
      hasRole([GROUP_BUSINESS_OWNERS, GROUP_SUNO]) ||
      Boolean(currentUser?.has_access_to_dashboards),
    [hasRole, currentUser?.has_access_to_dashboards]
  );

  const canAccessQBO = useMemo(
    () =>
      hasRole([GROUP_BUSINESS_OWNERS, GROUP_SUNO]) ||
      Boolean(currentUser?.has_access_to_qbo),
    [hasRole, currentUser?.has_access_to_qbo]
  );

  const canAccessCampaigns = useMemo(
    () =>
      hasRole([GROUP_BUSINESS_OWNERS, GROUP_SUNO]) ||
      Boolean(currentUser?.can_view_and_create_campaigns),
    [hasRole, currentUser?.can_view_and_create_campaigns]
  );

  // Business owners can always dispatch real processor (Tilled/Gravity) refunds;
  // for everyone else it is granted per-user via allow_tilled_refund. The
  // practice-level allow_tilled_refund master switch is applied by callers.
  const canIssueProcessorRefund = useMemo(
    () =>
      hasRole([GROUP_BUSINESS_OWNERS, GROUP_SUNO]) ||
      Boolean(currentUser?.allow_tilled_refund),
    [hasRole, currentUser?.allow_tilled_refund]
  );

  const canViewCatalog = useMemo(
    () =>
      isAdminOrBusinessOwner ||
      Boolean(currentUser?.can_view_catalog) ||
      Boolean(currentUser?.can_edit_catalog),
    [
      isAdminOrBusinessOwner,
      currentUser?.can_view_catalog,
      currentUser?.can_edit_catalog
    ]
  );

  const canEditCatalog = useMemo(
    () => isAdminOrBusinessOwner || Boolean(currentUser?.can_edit_catalog),
    [isAdminOrBusinessOwner, currentUser?.can_edit_catalog]
  );

  // ENG-2174. Mirrors the backend rule: the permission alone. No role bypass (Administrator,
  // Business Owner and Suno included), because practices hand out the admin role for unrelated
  // reasons.
  const canOverrideDedicatedSlots = Boolean(
    currentUser?.can_override_dedicated_slots
  );

  // Applies to everyone (no admin/business-owner bypass). Defaults to on when
  // the field is absent so behavior is unchanged until it is explicitly disabled.
  const canDownloadReports = useMemo(
    () => currentUser?.has_access_to_download_reports !== false,
    [currentUser?.has_access_to_download_reports]
  );

  // "Financial Reports Only Access" (Administration → Users → General Access): the user sees only
  // the Reports section, and only financial reports inside it. A dedicated backend column
  // (User.financial_reports_only_access — admin-writable via /staff/, read-only on /me).
  // Deliberately NOT bypassed on a switched session, unlike VIEW_OWN_* above: those are
  // data-scoping filters that lose meaning across practices, while this is an app-wide PHI
  // confinement on the user's own row — a multi-practice bookkeeper is exactly this feature's
  // persona, and switching practices must not hand them the full app.
  const isReportsOnlyUser = useMemo(
    () => Boolean(currentUser?.financial_reports_only_access),
    [currentUser?.financial_reports_only_access]
  );

  // Same default-on semantics: everyone can delete E-Documents files unless the
  // per-user grant is explicitly turned off (Administration → Users → Access).
  const canDeleteEdocuments = useMemo(
    () => currentUser?.able_to_delete_edocuments !== false,
    [currentUser?.able_to_delete_edocuments]
  );

  const memoizedCurrentUser = useMemo(() => {
    if (!currentUser) return null;
    return { ...currentUser, is_active: true };
  }, [currentUser]);

  return {
    isSunoAdminOrBusinessOwner,
    isAdminOrBusinessOwner,
    hasRole,
    userRoles,
    userId: currentUser?.id,
    isSwitchedSession,
    isSunoAdmin,
    isBusinessOwner,
    userPermissions,
    currentUser: memoizedCurrentUser,
    isAudiologist,
    isScheduler,
    canViewCosts,
    canAccessDashboards,
    canAccessQBO,
    canAccessCampaigns,
    canIssueProcessorRefund,
    canViewCatalog,
    canEditCatalog,
    canOverrideDedicatedSlots,
    canDownloadReports,
    canDeleteEdocuments,
    isReportsOnlyUser
  };
}
